/**
 * API Client Configuration
 * 환경별 API 클라이언트 설정
 */

import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios'
import { appConfig, log } from '@/shared/config/env'
import { useLanguageStore } from '@/shared/stores/languageStore'
import type { ApiResponse } from '@/shared/types/api.types'
import { getBackendApiBaseUrl, isLocalDevelopment } from '@/shared/config/domains'

// 재시도 설정
const MAX_RETRY_ATTEMPTS = 3
const RETRY_DELAY_MS = 2000 // 2초
const RETRYABLE_STATUS_CODES: number[] = [] // 재시도 비활성화

/**
 * 연속 실패해도 **로그인 화면으로 튕기지 않는** API 경로 조각.
 *
 * 길게 작성하는 폼(설립·PreSet·AI 문서)에서 네트워크가 잠깐 끊겼다고 전체 리로드로
 * 로그인 화면에 보내면, 임시저장하지 않은 입력이 통째로 사라진다. 에러는 그대로
 * 호출부로 돌려주고 화면만 유지한다.
 *
 * ⚠️ 조각은 슬래시로 감싸지 않는다. 예전에는 `/incorporation/` 이라 양쪽 슬래시를 요구해서
 *    `…/entity/incorporation-draft` 같은 DI e-Filing 경로가 그물에서 샜다(실측: 제출 중
 *    네트워크 오류 2회 → 로그인 화면으로 이동, 입력 소실).
 */
const NO_REDIRECT_ON_FAILURE = [
  '/preset/',
  'incorporation',              // incorporation-draft / -detail / -scaffold / -document-upload 포함
  '/aiDocuments/',
  '/marketplace/v1/malaysia/',  // 주소코드·이름검색·비서조회 등 같은 제출 흐름의 보조 호출
];

/**
 * API 클라이언트 클래스
 */
class ApiClient {
  private instance: AxiosInstance
  private consecutiveAuthFailures = 0 // 연속 인증 실패 추적
  // 로그인/로그아웃 등 세션 교체 시 증가시키는 세대 카운터
  private sessionGeneration = 0

  constructor() {
    // 로컬 개발 환경: Vite 프록시 사용 (/api)
    // 프로덕션/개발 서버: 도메인 매핑 적용 (포워드 방식 지원)
    const baseURL = isLocalDevelopment() 
      ? '/api'  // 로컬 개발: Vite 프록시 사용
      : getBackendApiBaseUrl();  // 서버 환경: 도메인 매핑 적용

    if (appConfig.debug) {
      log('debug', 'API Client initialized', {
        baseURL,
        currentHostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
        isLocalDev: isLocalDevelopment()
      });
    }

    this.instance = axios.create({
      baseURL,
      timeout: appConfig.apiTimeout,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // 세션 쿠키를 위해 필요
    })

    this.setupInterceptors()
    
    // 언어 스토어가 사용 가능한 경우에만 리스너 설정
    try {
      this.setupLanguageChangeListener()
    } catch (error) {
      console.warn('Language store not available, language headers will not be updated:', error)
    }
  }

  /**
   * 요청/응답 인터셉터 설정
   */
  private setupInterceptors(): void {
    // 요청 인터셉터
    this.instance.interceptors.request.use(
      (config) => {
        // T-Sign 서명 링크 조회는 브라우저 캐시를 타지 않게 한다.
        //
        // 만료 응답(410 Gone)은 HTTP 규격상 기본 캐시 대상이라, 만료 화면을 한 번
        // 본 브라우저는 발신자가 Remind 로 링크를 되살려도 캐시된 410 을 계속 쓴다.
        // 서버에 Cache-Control: no-store 를 넣었지만 그건 앞으로 받는 응답에만
        // 적용되고, 이미 캐시가 박힌 브라우저는 하드 리로드로도 풀리지 않는다
        // (하드 리로드는 문서·서브리소스만 우회하고 JS 가 쏘는 XHR 은 제외된다).
        // URL 을 매번 다르게 만들어 캐시 항목 자체를 비켜간다.
        try {
          const url = String(config.url || '')
          if (/^\/?tsign\/signing\/[^/]+\/?$/.test(url)) {
            config.params = { ...(config.params || {}), _ts: Date.now() }
          }
        } catch {
          // ignore
        }

        // 세대 정보를 헤더에 태워서 응답 처리 시 구 세대 요청을 식별
        try {
          config.headers = config.headers || {}
          // axios v1 header setter 호환
          if (typeof (config.headers as any).set === 'function') {
            ;(config.headers as any).set('X-Session-Gen', String(this.sessionGeneration))
          } else {
            ;(config.headers as any)['X-Session-Gen'] = String(this.sessionGeneration)
          }
        } catch {
          // ignore header set errors
        }

        // 현재 선택된 언어 정보를 헤더에 추가
        let currentLanguage = 'en' // 기본값
        try {
          currentLanguage = useLanguageStore().getCurrentLanguage()
          if (currentLanguage) {
            config.headers.set('Accept-Language', currentLanguage)
            config.headers.set('X-Language', currentLanguage)
          }
        } catch (error) {
          console.warn('Failed to get current language, using default:', error)
          config.headers.set('Accept-Language', 'en')
          config.headers.set('X-Language', 'en')
          currentLanguage = 'en'
        }

        if (appConfig.debug) {
          log('debug', `API Request: ${config.method?.toUpperCase()} ${config.url}`, {
            baseURL: config.baseURL,
            params: config.params,
            data: config.data,
            headers: config.headers,
            language: currentLanguage,
          })
        }
        return config
      },
      (error) => {
        log('error', 'Request Error:', error)
        return Promise.reject(error)
      }
    )

    // 응답 인터셉터
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // 성공 응답 시 연속 실패 카운터 리셋
        this.consecutiveAuthFailures = 0

        // 로그인/로그아웃 성공 시 세션 세대 증가
        try {
          const url = response.config?.url || ''
          if (url.includes('/auth/login')) {
            // 로그인 성공 응답 형태 다양한 경우를 위해 success/user 존재 여부 체크
            const data: any = response.data
            const success = !!(data?.success || data?.data?.user || data?.user)
            if (success) {
              this.sessionGeneration++
            }
          } else if (url.includes('/auth/logout')) {
            const data: any = response.data
            const success = !!(data?.success === true)
            if (success) {
              this.sessionGeneration++
            }
          }
        } catch {
          // ignore generation bump errors
        }

        if (appConfig.debug) {
          log('debug', `API Response: ${response.status} ${response.config.url}`, {
            data: response.data,
            headers: response.headers,
          })
        }
        return response
      },
      async (error: AxiosError) => {
        // 401 Unauthorized - 세션 만료 또는 인증 실패 (재시도 불필요)
        // 공개 접근 페이지 체크를 먼저 수행하여 불필요한 에러 로그 방지
        const currentPath = window.location.pathname;
        const isPublicAccessPage =
          currentPath.startsWith('/setup-entity/application') ||
          currentPath.startsWith('/custom-form/fill');
        const is401 = error.response?.status === 401;
        const is401OnPublicPage = is401 && isPublicAccessPage;
        
        const rawData = error.response?.data as any;
        const errorCode = rawData?.error?.code;
        const errorReason = rawData?.reason || rawData?.error?.reason;
        const status = error.response?.status;
        const urlForCheck = error.config?.url || '';
        const isAuthPage =
          currentPath.startsWith('/auth/login') ||
          currentPath.startsWith('/auth/register') ||
          currentPath.startsWith('/signup');
        const isAuthApi =
          urlForCheck.includes('/auth/login') ||
          urlForCheck.includes('/auth/register') ||
          urlForCheck.includes('/signup');

        const errorDetails = {
          status: error.response?.status,
          message: error.message,
          url: error.config?.url,
          data: rawData,
          errorCode,
          errorMessage: rawData?.error?.message,
        };

        // 🔐 엔티티/리소스/컨택트/레지스터 접근 제어 실패 공통 처리
        if (
          status === 403 &&
          errorCode === 'AUTH002' &&
          (errorReason === 'entity_access_denied' || errorReason === 'contact_access_denied' || errorReason === 'register_access_denied')
        ) {
          // 로그인/회원가입 화면/요청은 제외
          if (isAuthPage || isAuthApi) {
            return Promise.reject(error);
          }
          // Entity_my 등에서 사용하는 공통 다이얼로그(alert)로 표시 - 프론트 공통 문구로 통일
          let permissionMessage = 'You do not have permission to perform this action.';
          try {
            const { useTranslation } = await import('@/utils/locales/messages');
            const { createSyncT } = useTranslation();
            const t = createSyncT(useLanguageStore().getCurrentLanguage?.() || 'en');
            permissionMessage = t('access.PermissionDenied') || permissionMessage;
          } catch {
            // noop - fallback 유지
          }

          try {
            const { useDialog } = await import('@/shared/composables/useDialog');
            const { alert } = useDialog();

            await alert({
              title: 'Alert',
              message: permissionMessage,
              type: 'error'
            });
          } catch (dialogError) {
            console.warn('[API Client] Failed to use dialog alert, falling back to window.alert', dialogError);
            window.alert(permissionMessage);
          }

          // 접근 거부된 리소스 타입에 따라 해당 리스트 페이지로 리다이렉트
          if (errorReason === 'entity_access_denied') {
            // Entity_my 리스트 페이지로 이동
            window.location.href = '/Entity_my';
          } else if (errorReason === 'contact_access_denied') {
            // Contact 리스트 페이지로 이동
            window.location.href = '/Contact';
          } else if (errorReason === 'register_access_denied') {
            // Register 리스트 페이지로 이동
            window.location.href = '/register';
          } else {
            // 기타 경우 홈으로 이동
            window.location.href = '/';
          }

          return Promise.reject(error);
        }

        // 🔐 그 외 권한 부족(AUTH002) - 메뉴/버튼 권한 등 공통 다이얼로그 처리
        if (status === 403 && errorCode === 'AUTH002') {
          // 로그인/회원가입 화면/요청은 제외
          if (isAuthPage || isAuthApi) {
            return Promise.reject(error);
          }
          // 프론트 공통 문구로 통일
          let permissionMessage = 'You do not have permission to perform this action.';
          try {
            const { useTranslation } = await import('@/utils/locales/messages');
            const { createSyncT } = useTranslation();
            const t = createSyncT(useLanguageStore().getCurrentLanguage?.() || 'en');
            permissionMessage = t('access.PermissionDenied') || permissionMessage;
          } catch {
            // noop
          }

          // 이미 위에서 처리한 엔티티/컨택트/레지스터 접근 거부는 제외
          if (
            errorReason !== 'entity_access_denied' &&
            errorReason !== 'contact_access_denied' &&
            errorReason !== 'register_access_denied'
          ) {
            try {
              const { useDialog } = await import('@/shared/composables/useDialog');
              const { alert } = useDialog();

              await alert({
                title: 'Alert',
                message: permissionMessage,
                type: 'error',
              });
            } catch (dialogError) {
              console.warn(
                '[API Client] Failed to show alert for AUTH002, falling back to window.alert',
                dialogError
              );
              window.alert(permissionMessage);
            }
          }

          return Promise.reject(error);
        }

        // POSTAL001, POSTAL002 에러는 콘솔 로그 출력하지 않음
        // 공개 접근 페이지의 401 에러도 로그 출력하지 않음 (예상된 동작)
        if (errorCode !== 'POSTAL001' && errorCode !== 'POSTAL002' && !is401OnPublicPage) {
          log('error', 'Response Error:', errorDetails);
          console.error('API Error Details:', errorDetails);
        }

        if (is401) {
          if (isPublicAccessPage) {
            // 공개 접근 페이지에서는 401 에러를 조용히 처리 (리다이렉트 없음, 로그 없음)
            // 디버그 모드에서만 경고 메시지 출력
            if (appConfig.debug) {
              console.warn('[API Client] 401 Unauthorized on public access page - This is expected for unauthenticated users');
            }
            return Promise.reject(error);
          }
          
          // Malaysia SSM API는 401이 SSM 로그인 실패를 의미할 수 있으므로 리다이렉트하지 않음
          const url = error.config?.url || '';
          const isMalaysiaApi = url.includes('/marketplace/v1/malaysia/');

          // 풀싱크 진행/완료 시점의 연쇄 호출들은 401이어도 전역 로그아웃을 유발하지 않도록 예외 처리
          // - entity_my 하위의 진행 상황/요약/엔티티 정보/바인딩 등
          // - 재로그인 직후 이전 세션의 지연 응답이 섞여 들어와도 앱을 내리지 않음
          const isEntityMySafeDuringSync =
            url.includes('/entity_my/') &&
            (
              url.includes('/entity-info') ||
              url.includes('/entity-info-failures-summary') ||
              url.includes('/list') ||
              url.includes('/summary') ||
              url.includes('/bind') ||
              url.includes('/marketplace/v1/malaysia/full-sync/progress') ||
              url.includes('/marketplace/v1/malaysia/full-sync/active')
            );

          // 풀싱크 중에는 /auth/me 등의 세션 확인 401도 리다이렉트 금지
          const isAuthSessionEndpoint = url.includes('/auth/me') || url.includes('/auth/check') || url.includes('/session/');
          const suppressAuth401DueToFullSync = isAuthSessionEndpoint && this.isFullSyncLikelyActive();
          
          // 구 세대 요청에서 발생한 401은 무시 (전역 로그아웃 유발 금지)
          const reqGenHeader =
            (error.config as any)?.headers?.['X-Session-Gen'] ??
            (error.config as any)?.headers?.get?.('X-Session-Gen')
          const reqGen = Number(reqGenHeader)
          const isFromOldGeneration =
            Number.isFinite(reqGen) && reqGen < this.sessionGeneration
          
          // PreSet / Incorporation 관련 API는 401이어도 리다이렉트하지 않음
          const isPreSetApi = url.includes('/preset/') || url.includes('/incorporation/') || url.includes('/aiDocuments/');

          // 알림/공지 등 백그라운드 폴링 API는 모든 페이지에서 주기적으로 호출됨 →
          //   clientCd 미선택 등으로 401 이 나도 전역 로그아웃(→ 무한 reload)을 유발하면 안 됨.
          const isBackgroundPollApi =
            url.includes('/notifications/') || url.includes('/notification/') || url.includes('/notice/');

          // company-settings 는 getClientCd() 실패 시 401 → 어드민 세션/불완전 세션에서
          // 전역 로그아웃하면 로그인↔대시보드 루프가 난다 (httpOnly 쿠키는 JS로 안 지워짐).
          const isCompanySettingsApi = url.includes('/company-settings/');

          if (isMalaysiaApi || isEntityMySafeDuringSync || isFromOldGeneration || suppressAuth401DueToFullSync || isPreSetApi || isBackgroundPollApi || isCompanySettingsApi) {
            console.warn('[API Client] 401 suppressed - not redirecting to login', {
              url,
              isMalaysiaApi,
              isEntityMySafeDuringSync,
              isFromOldGeneration,
              suppressAuth401DueToFullSync,
              isCompanySettingsApi
            });
            // 리다이렉트하지 않고 에러만 반환
            return Promise.reject(error);
          }

          // MY Register 비즈니스 검증 오류(REGCRUD008)는 세션 만료가 아님 — 로그인 화면으로 보내지 않음
          const isRegisterBusinessError =
            errorCode === 'REGCRUD008'
            && (
              url.includes('/register/my/')
              || url.includes('/register/')
            );

          if (isRegisterBusinessError) {
            return Promise.reject(error);
          }

          // 추가 억제 로직 없이 일반적인 인증 실패 처리로 진행
          
          console.warn('[API Client] 401 Unauthorized - Session expired, redirecting to login');
          await this.handleAuthFailure(true); // 쿠키도 삭제 (세션 실제 무효)
          return Promise.reject(error)
        }

        // 재시도 가능한 에러인 경우 재시도 시도
        if (this.isRetryableError(error)) {
          const retryCount = (error.config as any)?.__retryCount || 0

          // 아직 재시도 횟수가 남아있으면 재시도
          if (retryCount < MAX_RETRY_ATTEMPTS) {
            console.log(`[API Client] Transient error detected (${error.response?.status || 'network'}), attempting retry...`)
            const retryResponse = await this.retryRequest(error)

            if (retryResponse) {
              // 재시도 성공
              console.log('[API Client] Retry successful')
              this.consecutiveAuthFailures = 0
              return retryResponse
            }
          }

          // 모든 재시도 실패
          const failUrl = error.config?.url || '';

          // 면제 경로는 **카운터도 올리지 않는다**.
          //   올리기만 하면 설립 폼에서 난 실패가 쌓였다가, 나중에 전혀 다른 API 가 처음
          //   실패하는 순간 임계치를 넘겨 로그인 화면으로 보내 버린다.
          if (NO_REDIRECT_ON_FAILURE.some((frag) => failUrl.includes(frag))) {
            console.warn('[API Client] Failure suppressed (no redirect) for:', failUrl);
            return Promise.reject(error);
          }

          this.consecutiveAuthFailures++
          console.warn(`[API Client] All retries failed. Consecutive failures: ${this.consecutiveAuthFailures}`)

          // 연속 2번 이상 실패 시에만 로그아웃 처리
          // 첫 번째 실패는 일시적 서버 재시작일 수 있으므로 허용
          if (this.consecutiveAuthFailures >= 2) {
            console.warn('[API Client] Multiple consecutive failures, redirecting to login (keeping cookies for recovery)');
            await this.handleAuthFailure(false); // 쿠키 유지 (세션 복구 가능)
          } else {
            console.log('[API Client] First consecutive failure, keeping session (server may be restarting)');
          }
        }

        return Promise.reject(error)
      }
    )
  }

  /**
   * 언어 변경 리스너 설정
   */
  private setupLanguageChangeListener(): void {
    useLanguageStore().onLanguageChange((newLanguage: string) => {
      if (appConfig.debug) {
        log('debug', `Language changed to: ${newLanguage}. API requests will now include this language.`)
      }
    })
  }

  /**
   * 지연 함수
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * 재시도 가능한 에러인지 확인
   */
  private isRetryableError(error: AxiosError): boolean {
    // 네트워크 에러 (서버 연결 불가)
    if (!error.response) {
      const networkErrors = ['ECONNREFUSED', 'ERR_NETWORK', 'ETIMEDOUT', 'ECONNRESET']
      if (networkErrors.includes(error.code || '') || error.message?.includes('Network Error')) {
        return true
      }
    }

    // 서버 에러 (502, 503, 504)
    if (error.response && RETRYABLE_STATUS_CODES.includes(error.response.status)) {
      return true
    }

    return false
  }

  /**
   * 현재 클라이언트에서 Full Sync가 진행 중인지 추정
   * - RegistrySyncModal이 사용하는 세션 스토리지 키 존재 여부로 판단
   */
  private isFullSyncLikelyActive(): boolean {
    try {
      if (typeof window === 'undefined') return false
      const key = 'entity_my_full_sync_mst_idx'
      const v = window.sessionStorage?.getItem?.(key)
      if (!v) return false
      const n = Number(v)
      return Number.isFinite(n) && n > 0
    } catch {
      return false
    }
  }

  /**
   * 요청 재시도
   */
  private async retryRequest(error: AxiosError): Promise<AxiosResponse | null> {
    const config = error.config
    if (!config) return null

    // 재시도 횟수 추적
    const retryCount = (config as any).__retryCount || 0

    if (retryCount >= MAX_RETRY_ATTEMPTS) {
      console.warn(`[API Client] Max retry attempts (${MAX_RETRY_ATTEMPTS}) reached`)
      return null
    }

    // 재시도 횟수 증가
    ;(config as any).__retryCount = retryCount + 1

    console.log(`[API Client] Retrying request (${retryCount + 1}/${MAX_RETRY_ATTEMPTS}) after ${RETRY_DELAY_MS}ms...`)
    await this.delay(RETRY_DELAY_MS)

    try {
      return await this.instance.request(config)
    } catch (retryError) {
      // 재시도도 실패한 경우 null 반환
      return null
    }
  }

  /**
   * 인증 실패 처리 - 세션 정리 및 로그인 페이지로 리다이렉트
   * @param clearCookies true면 쿠키도 삭제 (401 에러), false면 쿠키 유지 (네트워크 에러)
   */
  private async handleAuthFailure(clearCookies: boolean = true): Promise<void> {
    try {
      // 현재 경로가 이미 로그인 페이지거나 public 페이지면 스킵 (무한 루프 방지)
      const currentPath = window.location.pathname;
      if (currentPath.startsWith('/auth/login') || 
          currentPath.startsWith('/auth/register') || 
          currentPath.startsWith('/signup') ||
          currentPath.startsWith('/setup-entity/application')) {
        return;
      }

      // 세션 스토어 정리 (메모리 상태만 정리)
      const { useSessionStore } = await import('@/shared/stores/sessionStore');
      const sessionStore = useSessionStore();
      sessionStore.clearSession();
      sessionStore.isInitialized = false;

      // authStore 정리
      const { useAuthStore } = await import('@/features/auth/stores/authStore');
      const authStore = useAuthStore();

      if (clearCookies) {
        // 401 에러: 세션이 실제로 무효함 - 모든 데이터 정리 (쿠키 포함)
        console.log('[API Client] Session invalid (401), clearing all data including cookies');
        authStore.clearAllData();
      } else {
        // 네트워크 에러: 일시적 문제 - 쿠키는 유지하여 세션 복구 가능하게 함
        console.log('[API Client] Temporary error, keeping cookies for session recovery');
        // Pinia 상태만 정리 (쿠키는 유지)
        authStore.user = null;
        authStore.isInitialized = false;
        authStore.error = null;
      }

      // 로그인 페이지로 리다이렉트
      console.log('[API Client] Redirecting to login page...');
      window.location.href = '/auth/login';
    } catch (err) {
      console.error('[API Client] Failed to handle auth failure:', err);
      // 스토어 import 실패 시에도 로그인 페이지로 이동
      window.location.href = '/auth/login';
    }
  }

  /**
   * GET 요청
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.get<ApiResponse<T>>(url, config)
    return response.data
  }

  /**
   * POST 요청
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.post<ApiResponse<T>>(url, data, config)
    return response.data
  }

  /**
   * PUT 요청
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.put<ApiResponse<T>>(url, data, config)
    return response.data
  }

  /**
   * DELETE 요청
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.delete<ApiResponse<T>>(url, config)
    return response.data
  }

  /**
   * PATCH 요청
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.patch<ApiResponse<T>>(url, data, config)
    return response.data
  }

  /**
   * 기본 axios 인스턴스 반환
   */
  getInstance(): AxiosInstance {
    return this.instance
  }
}

// 싱글톤 인스턴스
export const apiClient = new ApiClient()

// 편의 함수들
export const api = {
  get: apiClient.get.bind(apiClient),
  post: apiClient.post.bind(apiClient),
  put: apiClient.put.bind(apiClient),
  delete: apiClient.delete.bind(apiClient),
  patch: apiClient.patch.bind(apiClient),
}

export default apiClient